import axios from 'axios';
import { createAsyncThunk, createSlice, PayloadAction, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { ISCOLARITE, defaultValue } from 'app/shared/model/scolarite.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<ISCOLARITE>,
  entity: defaultValue,
  updating: false,
  totalItems: 0,
};

const apiUrl = 'api/scolarite';

export const getScolarites = createAsyncThunk('scolarite/fetch_scolarite_list', async ({ ecoleId, anneeScolaireId }: ISCOLARITE) => {
  const requestUrl = `${apiUrl}/ecole/${ecoleId}?anneeScolaireId=${anneeScolaireId}`;
  return axios.get<ISCOLARITE[]>(requestUrl);
});

export const getScolarite = createAsyncThunk(
  'scolarite/fetch_scolarite',
  async (scolariteId: string | number) => {
    const requestUrl = `${apiUrl}/${scolariteId}`;
    const response = axios.get<ISCOLARITE>(requestUrl);
    return response;
  },
  { serializeError: serializeAxiosError }
);

export const initializeNewScolarite = createAsyncThunk(
  'scolarite/initialize_newScolarite',
  async (entity: ISCOLARITE, thunkAPI) => {
    const result = await axios.post<ISCOLARITE>(`${apiUrl}/initialize`, entity);
    thunkAPI.dispatch(getScolarites(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const campagneReinscription = createAsyncThunk(
  'scolarite/inscription_scolarite',
  async (entity: ISCOLARITE, thunkAPI) => {
    const result = await axios.post<ISCOLARITE>(
      `${apiUrl}/reinscription/${entity.ecoleId}?anneeScolaireId=${entity.anneeScolaireId}`,
      entity
    );
    thunkAPI.dispatch(getScolarites(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteScolarite = createAsyncThunk('scolarite/delete_scolarite', async (scolariteId: string | number, thunkAPI) => {
  try {
    const requestUrl = `${apiUrl}/${scolariteId}`;
    await axios.delete(requestUrl);
    return scolariteId;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// Création du slice Redux
export type ScolariteState = Readonly<typeof initialState>;
const ScolariteSlice = createSlice({
  name: 'scolarite',
  initialState: initialState as ScolariteState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getScolarites), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
      })
      .addMatcher(isFulfilled(getScolarite), (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(initializeNewScolarite), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(campagneReinscription), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isRejected(getScolarites), (state, action) => {
        state.loading = false;
      })
      .addMatcher(isPending(getScolarites, getScolarite), state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addMatcher(isPending(initializeNewScolarite, campagneReinscription), state => {
        state.errorMessage = null;
        state.updating = true;
      })
      .addMatcher(isFulfilled(deleteScolarite), (state, action) => {
        state.loading = false;
        state.entities = state.entities.filter(scolarite => scolarite.id !== action.payload);
      })
      .addMatcher(isRejected(deleteScolarite), (state, action) => {
        state.loading = false;
      })
      .addMatcher(isPending(deleteScolarite), state => {
        state.errorMessage = null;
        state.loading = true;
      });
  },
});

export const { reset } = ScolariteSlice.actions;

export default ScolariteSlice.reducer;
